import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  StorefrontOutlined as StoreIcon,
  PriceChangeOutlined as TemplateIcon,
  ImageOutlined as ImageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { searchCategories } from '../../utils/categorySearch';
import DashboardLayout from '../../layouts/DashboardLayout';
import { getApiBaseUrl, getAuthHeader } from '../../services/httpClient';
import { resolveCategoryThumbSrc } from '../../utils/locsySubcategoryThumbs';
import {
  CATALOG_THUMB_MAX_INPUT_BYTES,
  compressImageForUpload,
} from '../../utils/compressImageUpload';

const API_BASE_URL = getApiBaseUrl();

const FEATURE_TEMPLATE_OPTIONS = [
  { value: 'service_general', label: 'Service (general)' },
  { value: 'product_retail', label: 'Product / retail' },
  { value: 'online_center', label: 'Online center' },
  { value: 'education', label: 'Education' },
];

const emptyForm = {
  id: null,
  category_key: '',
  label: '',
  telugu_name: '',
  search_keywords: '',
  category_group: 'products',
  feature_template: 'product_retail',
  sort_order: 0,
  is_active: true,
  thumbnail_url: '',
};

const HOME_NEEDS_PRESETS = [
  {
    label: 'Cookers / Pressure Cookers',
    category_key: 'home_needs__cookers',
    category_group: 'products',
    feature_template: 'product_retail',
    sort_order: 210,
  },
  {
    label: 'Gas Stoves / Gas Cuts',
    category_key: 'home_needs__gas_stoves',
    category_group: 'products',
    feature_template: 'product_retail',
    sort_order: 211,
  },
  {
    label: 'Mixies / Grinders',
    category_key: 'home_needs__mixies',
    category_group: 'products',
    feature_template: 'product_retail',
    sort_order: 212,
  },
  {
    label: 'Fans (Ceiling / Table / Exhaust)',
    category_key: 'home_needs__fans',
    category_group: 'products',
    feature_template: 'product_retail',
    sort_order: 213,
  },
  {
    label: 'Iron Boxes',
    category_key: 'home_needs__irons',
    category_group: 'products',
    feature_template: 'product_retail',
    sort_order: 214,
  },
  {
    label: 'Water Heaters / Geysers',
    category_key: 'home_needs__water_heaters',
    category_group: 'products',
    feature_template: 'product_retail',
    sort_order: 215,
  },
  {
    label: 'Home Appliance Sales & Service',
    category_key: 'home_needs__home_appliance_service',
    category_group: 'services',
    feature_template: 'service_general',
    sort_order: 216,
  },
  {
    label: 'Gas Stove Repair / Service',
    category_key: 'home_needs__gas_stove_service',
    category_group: 'services',
    feature_template: 'service_general',
    sort_order: 217,
  },
];

const slugifyCategoryKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');

const thumbSrcForRow = (row) =>
  resolveCategoryThumbSrc({
    thumbnail_url: row?.thumbnail_url,
    categoryKey: row?.category_key,
    subCategoryId: row?.sub_category_id,
    mainCategoryId: row?.main_category_id,
    label: row?.label,
  });

const BusinessCategoryIndex = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pendingThumbFile, setPendingThumbFile] = useState(null);
  const [pendingThumbPreview, setPendingThumbPreview] = useState('');
  const [snack, setSnack] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    row: null,
    usage: null,
    loading: false,
    migrateToKey: 'miscellaneous__other',
  });

  const isEdit = Boolean(form.id);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/franchisee/admin/business-categories`, {
        headers: getAuthHeader(),
      });
      if (res.data?.success) {
        setRows(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setRows([]);
        setError(res.data?.message || 'Failed to load category index');
      }
    } catch (err) {
      setRows([]);
      setError(err.response?.data?.message || 'Failed to load category index');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    return () => {
      if (pendingThumbPreview) URL.revokeObjectURL(pendingThumbPreview);
    };
  }, [pendingThumbPreview]);

  const filteredRows = useMemo(() => {
    return searchCategories(rows.filter(row => groupFilter === 'all' || row.category_group === groupFilter), search);
  }, [rows, search, groupFilter]);

  const clearPendingThumb = () => {
    if (pendingThumbPreview) URL.revokeObjectURL(pendingThumbPreview);
    setPendingThumbFile(null);
    setPendingThumbPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const openAdd = () => {
    clearPendingThumb();
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    clearPendingThumb();
    setForm({
      id: row.id,
      category_key: row.category_key || '',
      label: row.label || '',
      telugu_name: row.telugu_name || '',
      search_keywords: (row.search_aliases || []).join(', '),
      category_group: row.category_group || 'services',
      feature_template: row.feature_template || 'service_general',
      sort_order: row.sort_order ?? 0,
      is_active: Boolean(row.is_active),
      thumbnail_url: row.thumbnail_url || '',
    });
    setDialogOpen(true);
  };

  const uploadThumbnailForId = async (categoryId, file) => {
    // Always compress+resize in browser before multipart upload
    const prepared = await compressImageForUpload(file);
    const fd = new FormData();
    fd.append('thumbnail', prepared);
    const res = await axios.post(
      `${API_BASE_URL}/franchisee/admin/business-categories/${categoryId}/thumbnail`,
      fd,
      {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    if (!res.data?.success) {
      throw new Error(res.data?.message || 'Thumbnail upload failed');
    }
    return res.data.data;
  };

  const handleSave = async () => {
    const label = form.label.trim();
    if (!label) {
      setSnack({ severity: 'error', message: 'Display label is required' });
      return;
    }
    setSaving(true);
    try {
      let savedRow = null;
      if (isEdit) {
        const payload = {
          label,
          telugu_name: form.telugu_name.trim(),
          search_aliases: form.search_keywords.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
          category_group: form.category_group,
          feature_template: form.feature_template,
          sort_order: Number(form.sort_order) || 0,
          is_active: Boolean(form.is_active),
        };
        if (!pendingThumbFile) {
          payload.thumbnail_url = form.thumbnail_url?.trim() || null;
        }
        const res = await axios.put(
          `${API_BASE_URL}/franchisee/admin/business-categories/${form.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        if (!res.data?.success) {
          throw new Error(res.data?.message || 'Could not update category');
        }
        savedRow = res.data.data;
      } else {
        const payload = {
          label,
          telugu_name: form.telugu_name.trim(),
          search_aliases: form.search_keywords.split(/[,\n]/).map(s => s.trim()).filter(Boolean),
          category_key:
            form.category_key.trim() || slugifyCategoryKey(label),
          category_group: form.category_group,
          feature_template: form.feature_template,
          sort_order: Number(form.sort_order) || 0,
        };
        if (form.thumbnail_url?.trim() && !pendingThumbFile) {
          payload.thumbnail_url = form.thumbnail_url.trim();
        }
        const res = await axios.post(
          `${API_BASE_URL}/franchisee/admin/business-categories`,
          payload,
          { headers: getAuthHeader() }
        );
        if (!res.data?.success) {
          throw new Error(res.data?.message || 'Could not add category');
        }
        savedRow = res.data.data;
      }

      if (pendingThumbFile && savedRow?.id) {
        setUploadingThumb(true);
        savedRow = await uploadThumbnailForId(savedRow.id, pendingThumbFile);
      }

      setSnack({
        severity: 'success',
        message: isEdit ? 'Category updated' : 'Category added to index',
      });
      setDialogOpen(false);
      clearPendingThumb();
      loadRows();
    } catch (err) {
      setSnack({
        severity: 'error',
        message:
          err.response?.data?.message ||
          err.message ||
          (isEdit ? 'Could not update category' : 'Could not add category'),
      });
    } finally {
      setSaving(false);
      setUploadingThumb(false);
    }
  };

  const handleThumbFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      setSnack({ severity: 'error', message: 'Choose an image file (PNG/JPG/WebP)' });
      return;
    }
    if (file.size > CATALOG_THUMB_MAX_INPUT_BYTES) {
      setSnack({
        severity: 'error',
        message: 'Image is over 12 MB — pick a smaller file (upload still auto-compresses to 512×512).',
      });
      return;
    }
    if (pendingThumbPreview) URL.revokeObjectURL(pendingThumbPreview);
    // Keep original for preview; compress runs automatically on save/upload
    setPendingThumbFile(file);
    setPendingThumbPreview(URL.createObjectURL(file));
  };

  const toggleActive = async (row) => {
    try {
      await axios.put(
        `${API_BASE_URL}/franchisee/admin/business-categories/${row.id}`,
        { is_active: !row.is_active },
        { headers: getAuthHeader() }
      );
      loadRows();
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.response?.data?.message || 'Update failed',
      });
    }
  };

  const migrateTargetOptions = useMemo(() => {
    const currentKey = deleteDialog.row?.category_key;
    return rows
      .filter((r) => r.is_active && r.category_key !== currentKey)
      .map((r) => ({
        value: r.category_key,
        label: `${r.label} (${r.category_key})`,
      }));
  }, [rows, deleteDialog.row]);

  const handleDelete = async (row) => {
    setDeleteDialog({
      open: true,
      row,
      usage: null,
      loading: true,
      migrateToKey: 'miscellaneous__other',
    });
    try {
      const res = await axios.get(
        `${API_BASE_URL}/franchisee/admin/business-categories/${row.id}/usage`,
        { headers: getAuthHeader() }
      );
      const usage = res.data?.data || null;
      setDeleteDialog((prev) => ({
        ...prev,
        usage,
        loading: false,
        migrateToKey: usage?.defaultMigrateToKey || 'miscellaneous__other',
      }));
    } catch {
      setDeleteDialog((prev) => ({
        ...prev,
        usage: { totalAffected: 0 },
        loading: false,
      }));
    }
  };

  const confirmDelete = async () => {
    const row = deleteDialog.row;
    if (!row) return;
    try {
      const migrateToKey =
        deleteDialog.migrateToKey || 'miscellaneous__other';
      const res = await axios.delete(
        `${API_BASE_URL}/franchisee/admin/business-categories/${row.id}`,
        {
          headers: getAuthHeader(),
          params: {
            migrate: true,
            migrateToKey,
          },
        }
      );
      setSnack({
        severity: 'success',
        message: res.data?.message || 'Removed',
      });
      setDeleteDialog({
        open: false,
        row: null,
        usage: null,
        loading: false,
        migrateToKey: 'miscellaneous__other',
      });
      loadRows();
    } catch (err) {
      setSnack({
        severity: 'error',
        message: err.response?.data?.message || 'Delete failed',
      });
    }
  };

  const previewSrc =
    pendingThumbPreview ||
    resolveCategoryThumbSrc({
      thumbnail_url: form.thumbnail_url,
      categoryKey: form.category_key,
      label: form.label,
    });

  return (
    <DashboardLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <StoreIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h5" fontWeight={800}>
                Shops & Services Index
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.95 }}>
                Full CRUD for registration categories — edit fields, upload thumbnails,
                manage templates and active status
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
          >
            <TextField
              size="small"
              placeholder="Search kirana, తాపీ, davakhana…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Group</InputLabel>
              <Select
                label="Group"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="services">Services</MenuItem>
                <MenuItem value="products">Products</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<RefreshIcon />} onClick={loadRows} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
              Add category
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/mitra-token-board-settings')}
            >
              Token Board defaults
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {filteredRows.length} of {rows.length} categories · Active items appear on
            registration. Use Edit to change label/group/template/thumbnail. Templates
            opens catalog pricing templates. Refresh syncs new built-ins from seed.
          </Typography>
        </Paper>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={56}>Thumb</TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Built-in</TableCell>
                <TableCell>Default template</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Avatar
                      variant="rounded"
                      src={thumbSrcForRow(row) || undefined}
                      imgProps={{
                        loading: 'lazy',
                        decoding: 'async',
                        onError: (e) => {
                          e.currentTarget.style.display = 'none';
                        },
                      }}
                      sx={{ width: 40, height: 40, bgcolor: 'grey.100' }}
                    >
                      <ImageIcon fontSize="small" color="disabled" />
                    </Avatar>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.label}{row.telugu_name && <Typography variant="caption" component="div" color="text.secondary">{row.telugu_name}</Typography>}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {row.category_key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.category_group}
                      color={row.category_group === 'products' ? 'success' : 'primary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={Boolean(row.is_active)}
                      onChange={() => toggleActive(row)}
                    />
                  </TableCell>
                  <TableCell>{row.is_builtin ? 'Yes' : 'Custom'}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TemplateIcon />}
                      onClick={() =>
                        navigate(
                          `/catalog-templates?categoryKey=${encodeURIComponent(row.category_key)}`,
                        )
                      }
                    >
                      Templates
                    </Button>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label="edit"
                      onClick={() => openEdit(row)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="delete"
                      onClick={() => handleDelete(row)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {rows.length === 0
                      ? 'No categories loaded. Click Refresh — built-in categories are seeded automatically on first load.'
                      : 'No categories match your search'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={() => !saving && setDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {isEdit ? 'Edit shop / service category' : 'Add shop / service category'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {!isEdit && (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.75, display: 'block' }}
                  >
                    Quick fill — Home Needs (tap one, then Save)
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
                    {HOME_NEEDS_PRESETS.map((preset) => (
                      <Chip
                        key={preset.category_key}
                        label={preset.label}
                        clickable
                        color={
                          form.category_key === preset.category_key ? 'primary' : 'default'
                        }
                        variant={
                          form.category_key === preset.category_key ? 'filled' : 'outlined'
                        }
                        onClick={() =>
                          setForm({
                            ...emptyForm,
                            ...preset,
                          })
                        }
                      />
                    ))}
                  </Stack>
                </Box>
              )}

              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  variant="rounded"
                  src={previewSrc || undefined}
                  sx={{ width: 72, height: 72, bgcolor: 'grey.100' }}
                >
                  <ImageIcon color="disabled" />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Thumbnail
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      size="small"
                      variant="outlined"
                      component="label"
                      startIcon={<ImageIcon />}
                      disabled={saving || uploadingThumb}
                    >
                      Upload image
                      <input
                        ref={fileInputRef}
                        hidden
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleThumbFileChange}
                      />
                    </Button>
                    {(pendingThumbFile || form.thumbnail_url) && (
                      <Button
                        size="small"
                        color="inherit"
                        disabled={saving}
                        onClick={() => {
                          clearPendingThumb();
                          setForm((f) => ({ ...f, thumbnail_url: '' }));
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Any size OK · auto-compressed to 512×512 (~KB) on upload. Saved with category.
                  </Typography>
                </Box>
              </Stack>

              <TextField
                label="Display label"
                fullWidth
                value={form.label}
                onChange={(e) => {
                  const label = e.target.value;
                  setForm((f) => ({
                    ...f,
                    label,
                    category_key: isEdit
                      ? f.category_key
                      : f.category_key &&
                          f.category_key !== slugifyCategoryKey(f.label)
                        ? f.category_key
                        : slugifyCategoryKey(label),
                  }));
                }}
                placeholder="Cookers / Pressure Cookers"
              />
              <TextField label="Telugu name" fullWidth value={form.telugu_name}
                onChange={e => setForm(f => ({ ...f, telugu_name: e.target.value }))}
                helperText="One short Telugu name shown beside the category." />
              <TextField label="Search Keywords / Local Names" fullWidth multiline minRows={3}
                value={form.search_keywords}
                onChange={e => setForm(f => ({ ...f, search_keywords: e.target.value }))}
                helperText="Separate phrases with commas. Used for search only; keywords are hidden from customers." />
              <TextField
                label="Category key (unique)"
                fullWidth
                value={form.category_key}
                disabled={isEdit}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    category_key: e.target.value.toLowerCase().replace(/\s+/g, '_'),
                  }))
                }
                placeholder="home_needs__cookers"
                helperText={
                  isEdit
                    ? 'Key cannot be changed after create'
                    : 'Optional custom key · Home Needs: home_needs__cookers'
                }
              />
              <FormControl fullWidth>
                <InputLabel>Group</InputLabel>
                <Select
                  label="Group"
                  value={form.category_group}
                  onChange={(e) => {
                    const category_group = e.target.value;
                    setForm((f) => ({
                      ...f,
                      category_group,
                      feature_template:
                        category_group === 'products'
                          ? 'product_retail'
                          : f.feature_template === 'product_retail'
                            ? 'service_general'
                            : f.feature_template,
                    }));
                  }}
                >
                  <MenuItem value="services">Services</MenuItem>
                  <MenuItem value="products">Products</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Feature template</InputLabel>
                <Select
                  label="Feature template"
                  value={form.feature_template}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, feature_template: e.target.value }))
                  }
                >
                  {FEATURE_TEMPLATE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Sort order"
                type="number"
                fullWidth
                value={form.sort_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))
                }
              />
              {isEdit && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.is_active)}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, is_active: e.target.checked }))
                      }
                    />
                  }
                  label="Active on registration"
                />
              )}
              <TextField
                label="Thumbnail URL (optional)"
                fullWidth
                value={form.thumbnail_url}
                disabled={Boolean(pendingThumbFile)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, thumbnail_url: e.target.value }))
                }
                placeholder="Or paste an existing image URL"
                helperText="Leave blank if uploading a file above"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving
                ? uploadingThumb
                  ? 'Uploading…'
                  : 'Saving…'
                : isEdit
                  ? 'Update'
                  : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialog.open}
          onClose={() =>
            !deleteDialog.loading &&
            setDeleteDialog({
              open: false,
              row: null,
              usage: null,
              loading: false,
              migrateToKey: 'miscellaneous__other',
            })
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {deleteDialog.row?.is_builtin
              ? 'Deactivate category'
              : 'Delete category'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography>
                <strong>{deleteDialog.row?.label}</strong>
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ display: 'block', fontFamily: 'monospace', mt: 0.5 }}
                >
                  {deleteDialog.row?.category_key}
                </Typography>
              </Typography>
              {deleteDialog.loading ? (
                <Typography color="text.secondary">
                  Checking linked shops…
                </Typography>
              ) : (
                <>
                  <Alert
                    severity={
                      (deleteDialog.usage?.totalAffected || 0) > 0
                        ? 'warning'
                        : 'info'
                    }
                  >
                    {(deleteDialog.usage?.totalAffected || 0) > 0
                      ? `${deleteDialog.usage.franchiseeAdminCount || 0} registration(s) and ${deleteDialog.usage.publishedShopCount || 0} published shop(s) use this category. They will be moved to the target below before ${deleteDialog.row?.is_builtin ? 'deactivation' : 'deletion'}.`
                      : 'No linked shops found. Safe to proceed.'}
                  </Alert>
                  {(deleteDialog.usage?.totalAffected || 0) > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Move shops to</InputLabel>
                      <Select
                        label="Move shops to"
                        value={deleteDialog.migrateToKey}
                        onChange={(e) =>
                          setDeleteDialog((prev) => ({
                            ...prev,
                            migrateToKey: e.target.value,
                          }))
                        }
                      >
                        {!migrateTargetOptions.some(
                          (o) => o.value === 'miscellaneous__other'
                        ) && (
                          <MenuItem value="miscellaneous__other">
                            Miscellaneous / Other (required fallback)
                          </MenuItem>
                        )}
                        {migrateTargetOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {deleteDialog.row?.is_builtin && (
                    <Typography variant="body2" color="text.secondary">
                      Built-in categories are deactivated (hidden from
                      registration), not permanently removed.
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setDeleteDialog({
                  open: false,
                  row: null,
                  usage: null,
                  loading: false,
                  migrateToKey: 'miscellaneous__other',
                })
              }
              disabled={deleteDialog.loading}
            >
              Cancel
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={confirmDelete}
              disabled={deleteDialog.loading}
            >
              {deleteDialog.row?.is_builtin
                ? 'Deactivate'
                : (deleteDialog.usage?.totalAffected || 0) > 0
                  ? 'Move & delete'
                  : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={Boolean(snack)}
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          message={snack?.message}
        />
      </Container>
    </DashboardLayout>
  );
};

export default BusinessCategoryIndex;
